/**
 * Utility functions for calculating lot centers (centroids) for number positioning
 */

interface Point {
  x: number;
  y: number;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Calculates the centroid of a polygon defined by points
 * @param points Array of polygon points
 * @returns Centroid point {x, y}
 */
export function calculatePolygonCentroid(points: Point[]): Point {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }

  if (points.length === 1) {
    return { ...points[0] };
  }

  // For polygon centroid, we use the Shoelace formula
  let area = 0;
  let x = 0;
  let y = 0;

  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const cross = points[i].x * points[j].y - points[j].x * points[i].y;

    area += cross;
    x += (points[i].x + points[j].x) * cross;
    y += (points[i].y + points[j].y) * cross;
  }

  if (area === 0) {
    // Degenerate polygon, return average of all points
    const avgX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
    const avgY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

    return { x: avgX, y: avgY };
  }

  return {
    x: x / (3 * area),
    y: y / (3 * area),
  };
}

/**
 * Calculates the center of a bounding box
 * @param boundingBox Box with x, y, width, height
 * @returns Center point {x, y}
 */
export function calculateBoundingBoxCenter(boundingBox: BoundingBox): Point {
  return {
    x: boundingBox.x + boundingBox.width / 2,
    y: boundingBox.y + boundingBox.height / 2,
  };
}

/**
 * Calculates lot center based on whether it has polygon points or bounding box
 * @param lot Lot object with either polygonPoints or boundingBox
 * @returns Center point {x, y}
 */
export function calculateLotCenter(lot: {
  polygonPoints?: Point[];
  boundingBox?: BoundingBox;
}): Point {
  if (lot.polygonPoints && lot.polygonPoints.length > 0) {
    return calculatePolygonCentroid(lot.polygonPoints);
  }

  if (lot.boundingBox) {
    return calculateBoundingBoxCenter(lot.boundingBox);
  }

  return { x: 0, y: 0 };
}

/**
 * Applies offset to a point
 * @param point Original point
 * @param offsetX X offset
 * @param offsetY Y offset
 * @returns Offset point
 */
export function applyOffset(
  point: Point,
  offsetX: number,
  offsetY: number
): Point {
  return {
    x: point.x + offsetX,
    y: point.y + offsetY,
  };
}

/**
 * Creates SVG transform string for lot number
 * @param center Center point of lot
 * @param offset Offset from center
 * @param scale Scale factor (1 = normal size)
 * @param rotation Rotation angle in degrees
 * @returns SVG transform string
 */
export function createLotNumberTransform(
  center: Point,
  offset: Point,
  scale = 1,
  rotation = 0
): string {
  const finalPosition = applyOffset(center, offset.x, offset.y);

  const transforms = [
    `translate(${finalPosition.x}, ${finalPosition.y})`,
    scale !== 1 ? `scale(${scale})` : "",
    rotation !== 0 ? `rotate(${rotation})` : "",
    `translate(${-finalPosition.x}, ${-finalPosition.y})`,
  ].filter(Boolean);

  return transforms.join(" ");
}

export const LOT_NUMBER_FONT_SIZE = 16;
export const LOT_SIZE_FONT_SIZE = 10;
export const LOT_SIZE_DEFAULT_SCALE = 0.6;

export function getLotLabelGap(
  numberScale = 1,
  sizeScale = LOT_SIZE_DEFAULT_SCALE
): number {
  return (
    (LOT_NUMBER_FONT_SIZE * numberScale + LOT_SIZE_FONT_SIZE * sizeScale) / 2 +
    1
  );
}

/**
 * Calculates visibility threshold zoom level for lot numbers
 * @param viewBoxWidth Width of the SVG viewBox
 * @param viewBoxHeight Height of the SVG viewBox
 * @returns Minimum zoom level for showing lot numbers
 */
export function getLotNumberVisibilityZoom(
  viewBoxWidth: number,
  viewBoxHeight: number
): number {
  // Show lot numbers when zoomed in to at least this level
  const baseSize = Math.max(viewBoxWidth, viewBoxHeight);

  return Math.max(0.5, 1000 / baseSize);
}
